import {
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'

const {
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxDropdown,
  ComboboxPopupSearch,
  ComboboxEmpty,
  ComboboxList,
  ComboboxCheckItem,
  PopoverRoot,
  PopoverTrigger,
  PopoverPortal,
  PopoverPositioner,
  PopoverPopup,
  MenuRoot,
  MenuTrigger,
  MenuPortal,
  MenuPositioner,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  SegmentedChip,
  TagInput,
  Input,
  FieldRoot,
  FieldLabel,
  FieldDescription
} = Base

export interface Option {
  value: string
  label: string
  detail?: string
}

// The one popover open in a rule list, keyed by rule and part; null when none is.
export type OpenKey = string | null

export type SetOpenKey = Dispatch<SetStateAction<OpenKey>>

// Closing clears only its own key, so a pick that opens the next popover is not undone.
export const bindOpen = (
  key: string,
  openKey: OpenKey,
  setOpenKey: SetOpenKey
) => ({
  open: openKey === key,
  onOpenChange: (open: boolean) =>
    setOpenKey((current) => (open ? key : current === key ? null : current))
})

interface Openable {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface TriggerProps {
  // A Base control to render the trigger as; a plain button without one.
  trigger?: ReactElement
  triggerLabel?: string
  triggerClassName?: string
  triggerContent: ReactNode
}

// Enter in the search picks the first match (the Root's autoHighlight).
export const SearchDropdown: React.FC<{
  label: string
  emptyText: string
  className: string
  children: ReactNode
}> = ({ label, emptyText, className, children }) => {
  const { t } = useTranslation()
  return (
    <ComboboxDropdown align="start" className={className}>
      <ComboboxPopupSearch
        placeholder={label}
        aria-label={label}
        clearLabel={t('rule_list.clear_search')}
      />
      <ComboboxEmpty>{emptyText}</ComboboxEmpty>
      {children}
    </ComboboxDropdown>
  )
}

// A searchable list: several picks keep it open with a box per row, one pick closes it.
export const OptionsPicker: React.FC<
  Openable &
    TriggerProps & {
      options: Option[]
      value: string[]
      multiple?: boolean
      onChange: (values: string[]) => void
    }
> = ({
  options,
  value,
  multiple = false,
  onChange,
  open,
  onOpenChange,
  trigger,
  triggerLabel,
  triggerClassName,
  triggerContent
}) => {
  const { t } = useTranslation()
  const labels = new Map(options.map((option) => [option.value, option.label]))
  const labelOf = (item: string) => labels.get(item) ?? item
  const root = {
    items: options.map((option) => option.value),
    itemToStringLabel: labelOf,
    autoHighlight: true,
    open,
    onOpenChange
  }
  const parts = (
    <>
      <ComboboxTrigger
        aria-label={triggerLabel}
        className={triggerClassName}
        render={trigger}
      >
        {triggerContent}
      </ComboboxTrigger>
      <SearchDropdown
        label={t('rule_list.search')}
        emptyText={t('rule_list.no_matches')}
        className="max-h-72 w-72"
      >
        <ComboboxList>
          {(item: string) => (
            <ComboboxCheckItem
              key={item}
              value={item}
              indicator={multiple ? 'checkbox' : 'check'}
            >
              {labelOf(item)}
            </ComboboxCheckItem>
          )}
        </ComboboxList>
      </SearchDropdown>
    </>
  )

  return multiple ? (
    <ComboboxRoot<string, true>
      multiple
      value={value}
      onValueChange={(next) => onChange(next)}
      {...root}
    >
      {parts}
    </ComboboxRoot>
  ) : (
    <ComboboxRoot<string>
      value={value[0] ?? null}
      onValueChange={(next) => onChange(next === null ? [] : [next])}
      {...root}
    >
      {parts}
    </ComboboxRoot>
  )
}

// The editor inside draws its own padding.
export const ValuePopover: React.FC<
  Openable &
    TriggerProps & {
      className?: string
      children: ReactNode
    }
> = ({
  open,
  onOpenChange,
  trigger,
  triggerLabel,
  triggerClassName,
  triggerContent,
  className = 'w-72',
  children
}) => (
  <PopoverRoot open={open} onOpenChange={onOpenChange}>
    <PopoverTrigger
      aria-label={triggerLabel}
      className={triggerClassName}
      render={trigger}
    >
      {triggerContent}
    </PopoverTrigger>
    <PopoverPortal>
      <PopoverPositioner sideOffset={6} align="start">
        <PopoverPopup padding="none" className={className}>
          {children}
        </PopoverPopup>
      </PopoverPositioner>
    </PopoverPortal>
  </PopoverRoot>
)

export const OperatorSegment: React.FC<
  Openable & {
    label: string
    value: string
    options: Option[]
    editable: boolean
    onChange: (value: string) => void
  }
> = ({ label, value, options, editable, open, onOpenChange, onChange }) => {
  const current =
    options.find((option) => option.value === value)?.label ?? value
  if (!editable)
    return (
      <SegmentedChip.Segment emphasis="muted">{current}</SegmentedChip.Segment>
    )
  return (
    <MenuRoot open={open} onOpenChange={onOpenChange}>
      <MenuTrigger
        aria-label={label}
        render={<SegmentedChip.Trigger emphasis="muted" />}
      >
        {current}
      </MenuTrigger>
      <MenuPortal>
        <MenuPositioner sideOffset={6} align="start">
          <MenuPopup>
            <MenuRadioGroup
              value={value}
              onValueChange={(next: string) => onChange(next)}
            >
              {options.map((option) => (
                <MenuRadioItem key={option.value} value={option.value}>
                  {option.label}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
          </MenuPopup>
        </MenuPositioner>
      </MenuPortal>
    </MenuRoot>
  )
}

// A list to pick from when there is one, otherwise a popover holding the editor.
export const ValueSegment: React.FC<
  Openable & {
    label: string
    summary: ReactNode
    title?: string
    // Its maximum width; the summary truncates past it.
    className: string
    editable: boolean
    list?: {
      options: Option[]
      value: string[]
      onChange: (values: string[]) => void
    }
    children?: ReactNode
  }
> = ({
  label,
  summary,
  title,
  className,
  editable,
  list,
  open,
  onOpenChange,
  children
}) => {
  if (!editable)
    return (
      <SegmentedChip.Segment className={className} title={title}>
        <span className="min-w-0 truncate">{summary}</span>
      </SegmentedChip.Segment>
    )
  const trigger = (
    <SegmentedChip.Trigger className={`${className} truncate`} title={title} />
  )
  return list ? (
    <OptionsPicker
      multiple
      {...list}
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      triggerLabel={label}
      triggerContent={summary}
    />
  ) : (
    <ValuePopover
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      triggerLabel={label}
      triggerContent={summary}
    >
      {children}
    </ValuePopover>
  )
}

// Free values (tags, postal codes, ids): commas split a paste into several.
export const TagsPicker: React.FC<{
  label: string
  values: string[]
  onChange: (values: string[]) => void
}> = ({ label, values, onChange }) => {
  const { t } = useTranslation()
  return (
    <FieldRoot className="p-3">
      <FieldLabel>{label}</FieldLabel>
      <TagInput
        autoFocus
        values={values}
        placeholder={t('rule_list.type_enter')}
        removeLabel={(value) => t('rule_list.remove_value', { value })}
        onChange={onChange}
      />
    </FieldRoot>
  )
}

// Amounts are written in currency units and stored in cents, the unit settings-api compares against.
export const ValuePicker: React.FC<{
  label: string
  kind: 'number' | 'money' | 'date'
  value: string
  onChange: (value: string) => void
  onDone: () => void
}> = ({ label, kind, value, onChange, onDone }) => {
  const { t } = useTranslation()
  const [text, setText] = useState(() =>
    kind === 'money' && value !== ''
      ? String(Number(value) / 100)
      : kind === 'date'
        ? value.replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$3-$2-$1')
        : value
  )
  const change = (next: string) => {
    setText(next)
    if (kind !== 'money') return onChange(next)
    const amount = Number(next.replace(',', '.'))
    onChange(
      next.trim() === '' || isNaN(amount)
        ? ''
        : String(Math.round(amount * 100))
    )
  }

  return (
    <FieldRoot className="p-3">
      <FieldLabel>{label}</FieldLabel>
      <Input
        autoFocus
        type={kind === 'date' ? 'date' : 'text'}
        inputMode={
          kind === 'date' ? undefined : kind === 'money' ? 'decimal' : 'numeric'
        }
        value={text}
        onChange={(event) => change(event.target.value)}
        onKeyDown={(event) => event.key === 'Enter' && onDone()}
      />
      {kind === 'money' ? (
        <FieldDescription>{t('rule_list.money_hint')}</FieldDescription>
      ) : null}
    </FieldRoot>
  )
}
